/**
 * ANCHOR MODE - Storage State Hook
 *
 * FT-75 FIX (2026-03-25)
 * Reverted FT-74's pendingValueRef pattern - it re-introduced equipment bleed.
 * The correct approach: write to localStorage FIRST, then forceUpdate so useMemo
 * re-reads fresh. No refs carrying stale data. No bypassing the fresh-read.
 *
 * FT-70 COMPLETE REWRITE (2026-03-25)
 *
 * Fixed cross-vessel data contamination by using the WORKING maintenance pattern:
 * - useMemo for synchronous reads (fresh on every storageKey change)
 * - useReducer for re-render triggering
 * - Write callbacks capture storageKey at queue time
 *
 * REMOVED:
 * - All FT-59 through FT-69 patches (previousStorageKeyRef, dataVesselIdRef, etc.)
 * - All debug logging ([FT-60], [FT-62], [FT-65], [FT-68], [FT-69], [SAFARI DEBUG])
 * - Render-phase state updates (FT-69 crash cause)
 *
 * PRESERVED:
 * - Debounced writes (50ms batch window)
 * - Immediate writes for critical modules
 * - Event emission (sr:storage:set)
 * - CRUD operations (addItem, updateItem, archiveItem, removeItem)
 * - Parallel storage / storageRouter integration
 * - Sync integration (merge from cloud)
 * - Wrapper record filtering
 * - Operations merge protection
 * - Equipment register migration (legacy double-vesselId keys)
 * - Org-scoped key handling
 */

import { useState, useEffect, useCallback, useRef, useMemo, useReducer } from 'react';
import { useVessel } from '../contexts/VesselContext';
import { devLog } from '../utils/devLog';
import * as storageRouter from '../lib/storageRouter';
import { getOrgId } from '../utils/getOrgId';
import { FLAGS } from '../config/featureFlags';
import { toOrgScopedKey } from '../lib/storage';
import { ORG_SCOPED_MODULES } from '../sync/moduleMap';
import { resolvePullMergeWinner } from '../sync/UniversalSyncBridge';

// FT-296: shared-id PULL timestamp gate (belt-and-braces mirror of the bridge
// delta-merge gate — shares the SAME resolvePullMergeWinner decision). Merges a
// cloud `incoming` array with `currentLocal`, keeping the LOCAL copy of a SHARED
// id ONLY when its timestamp is KNOWN and STRICTLY NEWER than cloud's. BLANK or
// EQUAL timestamps → CLOUD wins (so deletes/archives don't resurrect and
// idempotent re-pulls stay churn-free). Local-only ids (absent from cloud) are
// appended unchanged — this helper does NOT change delete/resurrection
// semantics, only the shared-id winner.
const mergeIncomingKeepNewerLocal = (incoming, currentLocal) => {
  const localArr = Array.isArray(currentLocal) ? currentLocal : [];
  const localById = new Map();
  localArr.forEach((item) => {
    if (item && item.id != null) localById.set(item.id, item);
  });
  const cloudIds = new Set();
  const resolved = (Array.isArray(incoming) ? incoming : []).map((cloudItem) => {
    if (!cloudItem || cloudItem.id == null) return cloudItem;
    cloudIds.add(cloudItem.id);
    const localItem = localById.get(cloudItem.id);
    if (!localItem) return cloudItem;
    return resolvePullMergeWinner(localItem, cloudItem).localWins ? localItem : cloudItem;
  });
  const localOnly = localArr.filter(
    (item) => item && item.id != null && !cloudIds.has(item.id)
  );
  return [...resolved, ...localOnly];
};

// FT-365 v3 Layer A#4: pending-flush registry + wipeInProgress flag.
// Every debounced-write timeout registers itself here so authCacheWipe can
// cancel them synchronously before purging LS. wipeInProgress blocks
// queueWrite from scheduling new flushes for the duration of a wipe.
// Both are module-level singletons — the reload in AuthGate.logout() replaces
// the module with a fresh singleton on next mount, resetting wipeInProgress.
const pendingFlushRegistry = new Set();
let wipeInProgress = false;

/**
 * FT-365 v3 Layer A#4: cancel every pending debounced write and block any
 * further scheduling until the tab reloads. Called from authCacheWipe.js
 * BEFORE the LS/IDB purge so nothing repolutes the newly-cleared caches.
 *
 * Returns a promise that resolves after a microtask so any synchronous
 * scheduling attempts in the same JS turn are drained before the caller
 * proceeds with purge.
 */
export function cancelAllPendingFlushesAndBlock() {
  wipeInProgress = true;
  try {
    pendingFlushRegistry.forEach((handle) => {
      try { clearTimeout(handle); } catch (_) { /* non-fatal */ }
    });
    pendingFlushRegistry.clear();
  } catch (_) { /* non-fatal */ }
  return Promise.resolve();
}

/**
 * FT-365 v3 Layer A#4: exposed for tests to reset the wipeInProgress flag
 * between test cases without simulating a full reload.
 * NOT for production code — the reload IS how the flag resets.
 */
export function __ft365ResetWipeInProgress() {
  wipeInProgress = false;
  pendingFlushRegistry.clear();
}

/**
 * FT-365 v3 Layer A#4: exposed for tests to inspect the flag.
 */
export function __ft365IsWipeInProgress() {
  return wipeInProgress;
}

// Helper to safely parse JSON
const parseJson = (str, fallback = null) => {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// OPERATIONS MERGE PROTECTION (2026-03-09)
export const OPERATIONS_MERGE_COLLECTIONS = new Set([
  'operations_voyages',
  'operations_checklists',
  'operations_checklist_templates',
  'operations_permits',
  'operations_main_logbook',
  'operations_bunkering',
  'pilot_transfers',
  'towing_operations',
  'diving_operations',
  'checklist_records',
  'operations_checklist_favourites',
  'operations_voyage_templates',
  'operations_daily_checks_custom',
  'operations_periodic_custom',
  // FT-306: protect against pull-clobber of freshly-created jobs before cloud push completes
  'maintenance_jobs',
  'operations_custom_templates'
]);

/**
 * WRAPPER RECORD FILTER (2026-03-08)
 * Detects and filters out sync wrapper records
 */
const isWrapperRecord = (item) => {
  if (!item || typeof item !== 'object') return true;
  if (item.data && item.data._isArray) return true;
  if (item.data && Array.isArray(item.data.items)) return true;
  if (item.id && typeof item.id === 'string' && /^[0-9a-f-]{36}_/.test(item.id)) return true;
  return false;
};

const filterWrapperRecords = (data) => {
  if (!Array.isArray(data)) return data;
  return data.filter(item => !isWrapperRecord(item));
};

/**
 * Hook for vessel-scoped localStorage with React state sync
 *
 * @param {string} baseKey - The base storage key (e.g., 'hours_of_rest')
 * @param {*} initialValue - Default value if nothing in storage
 * @param {Object} options - Optional configuration
 * @param {boolean} options.noScope - If true, don't add vessel suffix
 * @returns {{ value: *, setValue: Function, loaded: boolean }} - Current value and setter
 */
export const useStorageState = (baseKey, initialValue = null, options = {}) => {
  // Get vessel context for scoping
  const { currentVessel, isLoading: vesselLoading } = useVessel();
  const vesselId = currentVessel?.id || 'default';

  // Store initialValue in ref to prevent dependency instability
  const initialValueRef = useRef(initialValue);

  // Build storage key (vessel-scoped unless noScope is true)
  const storageKey = (() => {
    if (options.noScope) {
      return baseKey;
    }
    return `${baseKey}_${vesselId}`;
  })();

  // MIGRATION: Handle legacy double-vesselId keys for equipment register
  if (baseKey === 'equipment_register' && !options.noScope) {
    const legacyKey = `equipment_register_${vesselId}_${vesselId}`;
    const canonicalKey = storageKey;

    const legacyData = localStorage.getItem(legacyKey);
    const canonicalData = localStorage.getItem(canonicalKey);

    if (legacyData && !canonicalData) {
      localStorage.setItem(canonicalKey, legacyData);
      localStorage.removeItem(legacyKey);
    } else if (legacyData && canonicalData) {
      try {
        const legacy = JSON.parse(legacyData);
        const canonical = JSON.parse(canonicalData);
        const mergedMap = new Map();
        canonical.forEach(item => mergedMap.set(item.id, item));
        legacy.forEach(item => {
          if (!mergedMap.has(item.id)) {
            mergedMap.set(item.id, item);
          }
        });
        const merged = Array.from(mergedMap.values());
        localStorage.setItem(canonicalKey, JSON.stringify(merged));
        localStorage.removeItem(legacyKey);
      } catch (e) {
        // Migration failed, silent fail
      }
    }
  }

  // FT-75 FIX: Force re-renders using a version counter
  // Write to localStorage FIRST, then forceUpdate so useMemo re-reads fresh
  const [version, forceUpdate] = useReducer(x => x + 1, 0);

  // FT-243: IDB-loaded value, populated by the async rehydrate effect.
  // `undefined` means "not yet read"; `null` is a valid IDB value meaning "empty".
  // useMemo prefers this over localStorage when defined.
  const [idbValue, setIdbValue] = useState(undefined);

  // FT-243 + FT-75 + FT-70: Prefer the IDB-loaded value when available, fall back to
  // localStorage for backwards-compat (users with legacy LS-only data).
  // Re-runs on: vessel switch (storageKey), local write (version), or IDB hydrate (idbValue).
  // NOTE: filterWrapperRecords applied here so downstream listeners can write raw values
  // into setIdbValue without re-filtering — single point of filtering.
  const value = useMemo(() => {
    if (idbValue !== undefined) {
      return filterWrapperRecords(idbValue ?? initialValueRef.current);
    }
    // Backwards-compat fallback — legacy users with localStorage data only
    const orgScopedKey = toOrgScopedKey(storageKey);
    const raw = localStorage.getItem(orgScopedKey);
    const parsed = raw ? parseJson(raw, initialValueRef.current) : initialValueRef.current;
    return filterWrapperRecords(parsed);
  }, [storageKey, version, idbValue]);

  // FT-243: Reset IDB cache when storageKey changes (vessel switch) so the
  // next render falls back to localStorage until the async rehydrate completes.
  // Without this, vessel A's IDB data would flash on vessel B's screen briefly.
  useEffect(() => {
    setIdbValue(undefined);
  }, [storageKey]);

  // Keep valueRef synced for write callbacks
  const valueRef = useRef(value);
  valueRef.current = value;

  // Track whether initial hydration is complete
  // FT-243: loaded is true only after the first IDB read completes.
  // Consumers (useMaintenanceSchedule, useDrillRegister, useEquipmentRegister,
  // useMaintenanceJobsDomain) all use this as a `if (!loaded) return` gate
  // before auto-populating / migrating / syncing. Deferring those side effects
  // until real IDB data is available is the CORRECT semantics — see Decision 1.
  // Typical delay: <100ms after mount.
  const loaded = idbValue !== undefined;

  // Phase 2 Performance Fix: Queue for batched writes
  const writeQueueRef = useRef(null);
  const flushTimeoutRef = useRef(null);
  const queuedKeyRef = useRef(null);
  // FT-457: provenance of the currently-queued batched write (captured at queue
  // time, applied when the 50ms debounce fires). Undefined = genuine write.
  const writeSourceRef = useRef(undefined);

  // Safe batched write function (with immediate option for critical data)
  //
  // FT-457 (echo source B): `writeSource` is threaded onto the underlying
  // storageRouter.writeJSON call. handleHydrate passes 'cloud-hydrate' for every
  // cloud-hydrate persist so its sr:storage:set echo is dropped inside the
  // justPulled cooldown (never re-pushed). ALL other callers (setValue, save,
  // the LS-fallback flushers) pass nothing → genuine user writes stay UNTAGGED
  // and are therefore KEPT even inside the cooldown (fail-safe-KEEP).
  const queueWrite = useCallback((newValue, writeSource = undefined) => {
    // FT-365 v3 Layer A#4: refuse to schedule if a user-boundary wipe is in
    // progress. Without this, a synchronous save() call between wipe start
    // and window.location.reload() would resurrect the previous user's row.
    if (wipeInProgress) {
      return;
    }
    const writeOpts = writeSource !== undefined ? { source: writeSource } : undefined;
    // Write immediately for critical modules (no batching)
    const shouldWriteImmediately = options.immediate ||
      baseKey.startsWith('operations_') ||
      baseKey === 'voyages' ||
      baseKey === 'active_voyage' ||
      baseKey === 'crew' ||
      baseKey === 'incidents' ||
      baseKey === 'defects' ||
      baseKey === 'maintenance_history';

    if (shouldWriteImmediately) {
      // Cancel any pending batched write first
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }

      try {
        const orgScopedKey = toOrgScopedKey(storageKey);
        storageRouter.writeJSON(orgScopedKey, newValue, writeOpts).catch(error => {
          console.error(`[useStorageState] Write failed for ${baseKey}:`, error);
          // Fallback: Try direct localStorage write at the SAME org-scoped key
          // so the useMemo LS fallback (line 164) and the sr:storage:set
          // listener (line 759) both find it.
          try {
            localStorage.setItem(orgScopedKey, JSON.stringify(newValue));
          } catch (fallbackError) {
            console.error(`[useStorageState] Fallback write also failed for ${baseKey}:`, fallbackError);
          }
        });
      } catch (error) {
        // Handle QuotaExceededError
        if (error.name === 'QuotaExceededError') {
          console.warn('[useStorageState] Storage quota exceeded, attempting cleanup...');
          try {
            const voyageKeys = Object.keys(localStorage).filter(k => k.startsWith('operations_voyages_'));
            voyageKeys.forEach(key => {
              const voyages = JSON.parse(localStorage.getItem(key) || '[]');
              const completed = voyages.filter(v => v.status === 'completed');
              const active = voyages.filter(v => v.status !== 'completed');
              if (completed.length > 5) {
                const sorted = completed.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
                const cleaned = [...active, ...sorted.slice(0, 5)];
                localStorage.setItem(key, JSON.stringify(cleaned));
              }
            });
            localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(newValue));
          } catch (retryError) {
            console.error('[useStorageState] Write failed even after cleanup:', retryError);
            window.dispatchEvent(new CustomEvent('sr:storage:quota-exceeded', {
              detail: { key: storageKey, module: baseKey }
            }));
          }
        } else {
          console.error('[useStorageState] Immediate write failed:', error);
        }
      }
      return;
    }

    // Non-critical: Use batched write
    writeQueueRef.current = newValue;
    queuedKeyRef.current = storageKey; // Capture key at queue time
    writeSourceRef.current = writeSource; // FT-457: capture provenance at queue time

    // Cancel pending flush
    if (flushTimeoutRef.current) {
      // FT-365 v3 Layer A#4: also remove from registry so we don't leak handles
      pendingFlushRegistry.delete(flushTimeoutRef.current);
      clearTimeout(flushTimeoutRef.current);
    }

    // Schedule flush (50ms batch window)
    flushTimeoutRef.current = setTimeout(() => {
      // FT-365 v3 Layer A#4: unregister on fire so the Set stays bounded.
      pendingFlushRegistry.delete(flushTimeoutRef.current);
      if (wipeInProgress) {
        // Wipe raced us between schedule and fire — drop the write.
        writeQueueRef.current = null;
        queuedKeyRef.current = null;
        return;
      }
      if (writeQueueRef.current !== null) {
        // Block write if storage key changed (vessel switched)
        if (queuedKeyRef.current !== null && queuedKeyRef.current !== storageKey) {
          writeQueueRef.current = null;
          queuedKeyRef.current = null;
          return;
        }

        const valueToWrite = writeQueueRef.current;
        // FT-457: apply the provenance captured at queue time.
        const batchedOpts = writeSourceRef.current !== undefined ? { source: writeSourceRef.current } : undefined;

        const orgScopedKey = toOrgScopedKey(storageKey);
        storageRouter.writeJSON(orgScopedKey, valueToWrite, batchedOpts).catch(error => {
          console.error(`[useStorageState] Batched write failed for ${baseKey}:`, error);
          try {
            localStorage.setItem(orgScopedKey, JSON.stringify(valueToWrite));
          } catch (fallbackError) {
            console.error(`[useStorageState] Fallback batched write also failed for ${baseKey}:`, fallbackError);
          }
        });

        writeQueueRef.current = null;
        queuedKeyRef.current = null;
      }
    }, 50);
    // FT-365 v3 Layer A#4: register the debounced-write timeout so
    // cancelAllPendingFlushesAndBlock() can drain them on wipe start.
    pendingFlushRegistry.add(flushTimeoutRef.current);
  }, [storageKey, baseKey, options.immediate]);

  // Clear stale write queue when vessel changes
  useEffect(() => {
    if (writeQueueRef.current !== null) {
      writeQueueRef.current = null;
      queuedKeyRef.current = null;
    }
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
  }, [vesselId]);

  // Flush immediately on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }

      if (writeQueueRef.current !== null) {
        // Block write if storage key changed
        if (queuedKeyRef.current !== null && queuedKeyRef.current !== storageKey) {
          return;
        }
        const valueToWrite = writeQueueRef.current;

        const orgScopedKey = toOrgScopedKey(storageKey);
        storageRouter.writeJSON(orgScopedKey, valueToWrite).catch(error => {
          console.error(`[useStorageState] Unmount write failed for ${baseKey}:`, error);
          try {
            localStorage.setItem(orgScopedKey, JSON.stringify(valueToWrite));
            window.dispatchEvent(new CustomEvent('sr:storage:set', {
              detail: {
                module: baseKey,
                vesselId,
                key: orgScopedKey,
                value: valueToWrite,
                timestamp: Date.now(),
                backend: 'localStorage',
                resolvedKey: orgScopedKey,
                baseKey: baseKey
              }
            }));
          } catch (fallbackError) {
            console.error(`[useStorageState] CRITICAL: Unmount fallback write failed for ${baseKey}:`, fallbackError);
          }
        });
      }
    };
  }, [storageKey, baseKey, vesselId]);

  // Flush on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // FT-365 v3 Layer A#4: don't flush during a user-boundary wipe.
        if (wipeInProgress) return;
        if (flushTimeoutRef.current) {
          pendingFlushRegistry.delete(flushTimeoutRef.current);
          clearTimeout(flushTimeoutRef.current);
        }
        if (writeQueueRef.current !== null) {
          if (queuedKeyRef.current !== null && queuedKeyRef.current !== storageKey) {
            return;
          }
          try {
            // FT-365 v3 Layer C (site 10): write at the SAME org-scoped key that
            // setValue / queueWrite / useMemo LS-fallback use. Writing at plain
            // `storageKey` here was the "row 7 resurrection" vector — the wipe
            // clears the org-scoped key but visibility-hidden then re-writes
            // the previous user's state to a plain vessel-scoped key.
            localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(writeQueueRef.current));
            writeQueueRef.current = null;
            queuedKeyRef.current = null;
          } catch (error) {
            console.error('[useStorageState] Failed to flush on visibility change:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [storageKey]);

  // Handle beforeunload for hard navigations
  useEffect(() => {
    const handleBeforeUnload = () => {
      // FT-365 v3 Layer A#4: don't flush during a user-boundary wipe.
      if (wipeInProgress) return;
      if (flushTimeoutRef.current) {
        pendingFlushRegistry.delete(flushTimeoutRef.current);
        clearTimeout(flushTimeoutRef.current);
      }
      if (writeQueueRef.current !== null) {
        if (queuedKeyRef.current !== null && queuedKeyRef.current !== storageKey) {
          return;
        }
        try {
          // FT-365 v3 Layer C (site 8): use org-scoped key. See visibilitychange note above.
          localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(writeQueueRef.current));
          writeQueueRef.current = null;
          queuedKeyRef.current = null;
        } catch (error) {
          // Silent fail in beforeunload
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [storageKey]);

  // Handle pagehide for Safari Mobile
  useEffect(() => {
    const handlePageHide = () => {
      // FT-365 v3 Layer A#4: don't flush during a user-boundary wipe.
      if (wipeInProgress) return;
      if (flushTimeoutRef.current) {
        pendingFlushRegistry.delete(flushTimeoutRef.current);
        clearTimeout(flushTimeoutRef.current);
      }
      if (writeQueueRef.current !== null) {
        if (queuedKeyRef.current !== null && queuedKeyRef.current !== storageKey) {
          return;
        }
        try {
          // FT-365 v3 Layer C (site 9): use org-scoped key. See visibilitychange note above.
          localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(writeQueueRef.current));
          writeQueueRef.current = null;
          queuedKeyRef.current = null;
        } catch (error) {
          // Silent fail in pagehide
        }
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [storageKey]);

  // FT-243 REWRITE: Re-hydration when vessel changes — IndexedDB-first reads.
  // Writes to idbValue state (not valueRef.current) so useMemo can pick it up.
  // Cancellation guard prevents stale storageKey results from overwriting newer ones.
  //
  // INVARIANT: storageRouter.readJSON never returns undefined — it returns either
  // the stored value, the LS fallback (storage.js:189-211 returns parsed JSON or
  // initialValueRef.current), or initialValueRef.current itself. So `next ?? null`
  // collapses only the `null` case to our "empty but read" sentinel.
  useEffect(() => {
    let cancelled = false;
    const rehydrate = async () => {
      // FT-243 (iter 3): Read at the ORG-SCOPED key — same key the sync bridge writes
      // (UniversalSyncBridge.js:789–812 calls toOrgScopedKey before writeJSON).
      // FT-365 v3 Layer B#3: enforce org-scope on the initial rehydrate so any
      // row that survived a failed wipe (mismatched orgId in the key) is rejected
      // and the hook falls back to its initialValue cleanly.
      const orgScopedKey = toOrgScopedKey(storageKey);
      // FT-368: enforce org-scope ONLY when the RESOLVED key genuinely carries an
      // orgId. FT-365 v3 Layer B#3 hard-coded `enforceOrgScope: true` here, which
      // rejected every plain (non-org) key — including the per-user identity key
      // `current_user` (created with { noScope:true }; stays plain after
      // toOrgScopedKey) — back to its initialValue (DEFAULT_USER, role 'crew'),
      // silently downgrading every non-crew user after hydration. Keying off the
      // resolved key's shape keeps FULL FT-365 enforcement for genuinely
      // org-scoped keys (incl. the noScope org modules fleet_crew /
      // fleet_risk_assessments / hours_of_rest, which resolve to `module_{orgId}`)
      // and exempts only truly-plain identity keys the guard could never enforce.
      const enforceOrgScope = storageRouter.isOrgScopedKey(orgScopedKey);
      let next = await storageRouter.readJSON(orgScopedKey, initialValueRef.current, { enforceOrgScope });

      // Forward-migration of legacy IDB data written under the PLAIN key before
      // this fix shipped. Only attempted if org-scoped read returned nothing
      // (initialValueRef.current sentinel) AND the keys actually differ
      // (i.e. ORG_SCOPED_STORAGE is enabled). One-shot — the next write goes to
      // the org-scoped key and the plain key is orphaned forever.
      if (
        (next === null || next === initialValueRef.current) &&
        orgScopedKey !== storageKey
      ) {
        try {
          const legacy = await storageRouter.readJSON(storageKey, null);
          if (legacy != null) {
            next = legacy;
            // Best-effort migrate forward — no await on the catch to avoid
            // blocking render. Failure here is non-fatal; the data is still
            // returned to the UI from `legacy`.
            storageRouter.writeJSON(orgScopedKey, legacy).catch((e) => {
              console.warn('[useStorageState] Legacy IDB migration write failed', e);
            });
          }
        } catch (e) {
          console.warn('[useStorageState] Legacy IDB migration read failed', e);
        }
      }

      if (cancelled) return;

      if (process.env.NODE_ENV !== 'production' && baseKey === 'hours_of_rest' && next != null) {
        devLog('[useStorageState] HOR data loaded from storage (IndexedDB-first)');
      }

      setIdbValue(next ?? null);
    };

    rehydrate();
    return () => { cancelled = true; };
  }, [storageKey, baseKey]);

  // Main setter that handles both direct values and function updaters
  const setValue = useCallback((updater) => {
    // Get current value from ref
    const prev = valueRef.current;

    // Resolve function updaters BEFORE saving
    const next = typeof updater === 'function' ? updater(prev) : updater;

    // Debug maintenance saves
    if (process.env.NODE_ENV !== 'production' && baseKey === 'maintenance_history') {
      devLog('[useStorageState] setValue called for maintenance_history');
      devLog('[useStorageState] Previous:', Array.isArray(prev) ? prev.length : 'not array');
      devLog('[useStorageState] Next:', Array.isArray(next) ? next.length : 'not array');
    }

    // Minimal logging for HOR debugging
    if (process.env.NODE_ENV !== 'production' && baseKey === 'hours_of_rest' && next?.buckets) {
      const count = Object.values(next.buckets).flat().length;
      devLog(`[HOR] Saving ${count} records`);
    }

    // FT-75 + FT-243: Write to localStorage FIRST, push to idbValue so useMemo
    // reads the new value synchronously, then queue the async IDB write.
    // Without the setIdbValue call the UI would render the prior idbValue until
    // sr:storage:set fires from the async IDB write — visible blink on saves.
    // Step 1: Write to localStorage immediately (synchronously)
    try {
      const orgScopedKey = toOrgScopedKey(storageKey);
      localStorage.setItem(orgScopedKey, JSON.stringify(next));
    } catch (error) {
      console.error(`[useStorageState] Failed to write ${storageKey}:`, error);
    }

    // Step 2: Update ref for callbacks
    valueRef.current = next;

    // Step 3: Push to idbValue so useMemo renders the new value on this tick.
    // null sentinel preserved for "empty but written".
    setIdbValue(next ?? null);

    // Step 4: Queue for async storage (IndexedDB, cloud sync, etc.)
    queueWrite(next);

    // Step 5: Force re-render — belt-and-braces; setIdbValue should also trigger this,
    // but keeping forceUpdate preserves the legacy invariant in case React batches.
    forceUpdate();

    if (process.env.NODE_ENV !== 'production') {
      devLog(`[useStorageState] Saved to ${storageKey}:`, Array.isArray(next) ? `${next.length} items` : 'data');
    }

    return next;
  }, [baseKey, storageKey, queueWrite, forceUpdate]);

  // Listen for cloud hydrate events
  useEffect(() => {
    const handleHydrate = (event) => {
      const detail = event.detail || {};

      const isOrgScoped = ORG_SCOPED_MODULES.has(baseKey);
      if (detail.module !== baseKey) {
        return;
      }
      if (!isOrgScoped && detail.vesselId !== vesselId) {
        return;
      }

      const incoming = detail.value;
      if (incoming == null) {
        if (process.env.NODE_ENV !== 'production') {
          devLog('[useStorageState] Ignoring null hydrate for:', baseKey);
        }
        return;
      }

      // Read local data from localStorage using CURRENT storageKey
      const getLocalDataFromStorage = () => {
        try {
          const orgScopedKey = toOrgScopedKey(storageKey);
          const raw = localStorage.getItem(orgScopedKey);
          return raw ? JSON.parse(raw) : [];
        } catch (e) {
          console.warn(`[useStorageState] Failed to read localStorage for ${storageKey}:`, e);
          return [];
        }
      };

      // UNIVERSAL EMPTY-OVERWRITE GUARD
      // NEVER allow empty array from sync to overwrite non-empty local data
      if (Array.isArray(incoming) && incoming.length === 0) {
        const currentLocal = getLocalDataFromStorage();
        if (Array.isArray(currentLocal) && currentLocal.length > 0) {
          console.warn(`[useStorageState] BLOCKED empty overwrite for ${baseKey} (${currentLocal.length} local items)`);
          queueWrite(currentLocal, 'cloud-hydrate'); // FT-457: hydrate-triggered re-persist of local — tag so its echo is dropped in cooldown (harmless LWW re-write)
          return;
        }
      }

      // DRILLS MODULE MERGE PROTECTION
      if (baseKey === 'drills' && Array.isArray(incoming)) {
        const currentLocal = getLocalDataFromStorage();
        const isEmptyCloud = incoming.length === 0;
        const hasLocalData = currentLocal.length > 0;

        if (isEmptyCloud && hasLocalData) {
          console.warn(`[useStorageState] BLOCKED empty overwrite for drills - local has ${currentLocal.length} items, cloud is empty`);
          queueWrite(currentLocal, 'cloud-hydrate'); // FT-457: hydrate-triggered re-persist of local — tag so its echo is dropped in cooldown (harmless LWW re-write)
          return;
        }

        const cloudIds = new Set(incoming.map(item => item.id));
        const localOnly = currentLocal.filter(item => !cloudIds.has(item.id));
        const merged = [...incoming, ...localOnly];

        // FT-243: Push merged result into idbValue so useMemo renders it immediately.
        // queueWrite is still async (writes to IDB); without setIdbValue the UI
        // would not see the merge until the async write + sr:storage:set roundtrip.
        // FT-457 (echo source B): tag as cloud-hydrate so the echo is dropped.
        queueWrite(merged, 'cloud-hydrate');
        valueRef.current = merged;
        setIdbValue(merged);
        forceUpdate();
        return;
      }

      // MAINTENANCE MODULE MERGE PROTECTION
      if (baseKey === 'maintenance_history' && Array.isArray(incoming)) {
        const currentLocal = getLocalDataFromStorage();
        const isEmptyCloud = incoming.length === 0;
        const hasLocalData = currentLocal.length > 0;

        if (isEmptyCloud && hasLocalData) {
          console.warn(`[useStorageState] BLOCKED empty overwrite for maintenance_history - local has ${currentLocal.length} items, cloud is empty`);
          queueWrite(currentLocal, 'cloud-hydrate'); // FT-457: hydrate-triggered re-persist of local — tag so its echo is dropped in cooldown (harmless LWW re-write)
          return;
        }

        const localById = new Map(currentLocal.map(item => [item.id, item]));
        const cloudIds = new Set(incoming.map(item => item.id));
        const localOnly = currentLocal.filter(item => !cloudIds.has(item.id));

        const mergedFromCloud = incoming.map(cloudItem => {
          const localItem = localById.get(cloudItem.id);
          if (!localItem) return cloudItem;
          return {
            ...cloudItem,
            ...(cloudItem.jobId === null || cloudItem.jobId === undefined) && localItem.jobId ? { jobId: localItem.jobId } : {}
          };
        });

        const merged = [...mergedFromCloud, ...localOnly];

        // FT-243 (iter 3): write at org-scoped key — matches what setValue/queueWrite
        // write, what useMemo's LS fallback reads, and what the sr:storage:set listener
        // compares against.
        localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(merged));
        valueRef.current = merged;
        setIdbValue(merged);
        forceUpdate();
        return;
      }

      // INCIDENTS MODULE MERGE PROTECTION
      if (baseKey === 'incidents' && Array.isArray(incoming)) {
        const currentLocal = getLocalDataFromStorage();
        const isEmptyCloud = incoming.length === 0;
        const hasLocalData = currentLocal.length > 0;

        if (isEmptyCloud && hasLocalData) {
          console.warn(`[useStorageState] BLOCKED empty overwrite for incidents - local has ${currentLocal.length} items, cloud is empty`);
          queueWrite(currentLocal, 'cloud-hydrate'); // FT-457: hydrate-triggered re-persist of local — tag so its echo is dropped in cooldown (harmless LWW re-write)
          return;
        }

        const cloudIds = new Set(incoming.map(item => item.id));
        const localOnly = currentLocal.filter(item => !cloudIds.has(item.id));
        const merged = [...incoming, ...localOnly];

        // FT-243 (iter 3): write at org-scoped key — matches what setValue/queueWrite
        // write, what useMemo's LS fallback reads, and what the sr:storage:set listener
        // compares against.
        localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(merged));
        valueRef.current = merged;
        setIdbValue(merged);
        forceUpdate();
        return;
      }

      // OPERATIONS MERGE PROTECTION
      if (OPERATIONS_MERGE_COLLECTIONS.has(baseKey) && Array.isArray(incoming)) {
        const currentLocal = getLocalDataFromStorage();
        const isEmptyCloud = incoming.length === 0;
        const hasLocalData = currentLocal.length > 0;

        if (isEmptyCloud && hasLocalData) {
          console.warn(`[useStorageState] BLOCKED empty overwrite for ${baseKey} - local has ${currentLocal.length} items, cloud is empty`);
          queueWrite(currentLocal, 'cloud-hydrate'); // FT-457: hydrate-triggered re-persist of local — tag so its echo is dropped in cooldown (harmless LWW re-write)
          return;
        }

        // FT-296: shared-id timestamp gate (belt-and-braces mirror of the bridge
        // delta-merge gate). Keeps a strictly-newer local edit from being
        // clobbered by an older cloud copy; blank/equal ts → cloud wins.
        const merged = mergeIncomingKeepNewerLocal(incoming, currentLocal);

        // FT-243 (iter 3): write at org-scoped key — matches what setValue/queueWrite
        // write, what useMemo's LS fallback reads, and what the sr:storage:set listener
        // compares against.
        localStorage.setItem(toOrgScopedKey(storageKey), JSON.stringify(merged));
        valueRef.current = merged;
        setIdbValue(merged);
        forceUpdate();
        return;
      }

      // MAINTENANCE CONFIG PROTECTION
      if (baseKey === 'maintenance_config') {
        let currentLocal = {};
        try {
          const orgScopedKey = toOrgScopedKey(storageKey);
          const raw = localStorage.getItem(orgScopedKey);
          currentLocal = raw ? JSON.parse(raw) : {};
        } catch (e) {
          console.warn(`[useStorageState] Failed to read maintenance_config from localStorage:`, e);
        }
        const hasLocalData = currentLocal && typeof currentLocal === 'object' && Object.keys(currentLocal).length > 0;
        const isEmptyCloud = !incoming || (typeof incoming === 'object' && Object.keys(incoming).length === 0);

        if (isEmptyCloud && hasLocalData) {
          console.warn(`[useStorageState] BLOCKED empty overwrite for maintenance_config - local has data, cloud is empty`);
          queueWrite(currentLocal, 'cloud-hydrate'); // FT-457: hydrate-triggered re-persist of local — tag so its echo is dropped in cooldown (harmless LWW re-write)
          return;
        }
      }

      // Normal behavior for all other modules
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useStorageState] Applying hydrate for:', baseKey);
      }

      // FT-243: see Branch A note above. queueWrite is async to IDB; setIdbValue
      // updates the render path synchronously.
      // FT-457 (echo source B): tag as cloud-hydrate so the echo is dropped in
      // the justPulled cooldown, never re-pushed.
      queueWrite(incoming, 'cloud-hydrate');
      valueRef.current = incoming;
      setIdbValue(incoming);
      forceUpdate();
    };

    window.addEventListener('sr:sync:hydrate', handleHydrate);
    return () => window.removeEventListener('sr:sync:hydrate', handleHydrate);
  }, [baseKey, vesselId, storageKey, queueWrite, forceUpdate]);

  // FT-243 UPDATE: Listen for storage changes from other tabs (native `storage` event).
  // Cross-tab writes hit localStorage in the other tab — the new value is in event.newValue.
  // Parse it, then refresh idbValue so useMemo re-renders.
  //
  // We pass the PARSED-but-UNFILTERED value to setIdbValue. The useMemo (Change 1)
  // applies filterWrapperRecords. Do NOT filter here — single point of filtering.
  useEffect(() => {
    // Cross-tab writes hit localStorage with the org-scoped key (storage.js:217–241
    // calls toOrgScopedKey internally before setItem). Match on the same key here
    // so we catch them.
    const orgScopedKey = toOrgScopedKey(storageKey);
    const handleStorageChange = (event) => {
      if (event.key === orgScopedKey && event.newValue) {
        const next = parseJson(event.newValue, initialValueRef.current);
        // next can be any JSON shape; null is acceptable ("empty but read").
        setIdbValue(next ?? null);
        if (process.env.NODE_ENV !== 'production') {
          devLog('[useStorageState] Updated from other tab for:', baseKey);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey, baseKey]);

  // FT-243 UPDATE: Listen for sr:storage:set events from same tab. The router and
  // IDB backend dispatch these on every write. We refresh idbValue so useMemo
  // re-renders with the fresh value.
  //
  // NOTE: filterWrapperRecords is NOT called here — it's now applied inside the
  // useMemo (Change 1). Calling it here would double-filter; omitting it from
  // useMemo would skip filtering on this path. Keep filtering in exactly one place.
  useEffect(() => {
    const handleSameTabStorageSet = (event) => {
      const detail = event.detail || {};
      const orgScopedKey = toOrgScopedKey(storageKey);
      if (detail.key !== orgScopedKey) {
        return;
      }

      const incoming = detail.value;
      if (incoming == null) {
        return;
      }

      // Compare against current rendered value to skip no-op updates.
      // valueRef.current still tracks the rendered value (see line 162 — unchanged).
      const currentJson = JSON.stringify(valueRef.current);
      const incomingFiltered = filterWrapperRecords(incoming);
      const incomingJson = JSON.stringify(incomingFiltered);
      if (currentJson === incomingJson) {
        return;
      }

      // Pass the RAW value (not filtered) to setIdbValue — the useMemo will filter.
      setIdbValue(incoming);
    };

    window.addEventListener('sr:storage:set', handleSameTabStorageSet);
    return () => window.removeEventListener('sr:storage:set', handleSameTabStorageSet);
  }, [storageKey, baseKey]);

  // Return API compatible with existing code
  const api = { value, setValue, loaded };

  // For array destructuring [value, setValue]
  Object.defineProperty(api, '0', { get: () => value });
  Object.defineProperty(api, '1', { get: () => setValue });
  Object.defineProperty(api, 'length', { value: 2 });

  return api;
};

// Default export for compatibility
export default { useStorageState };
